import { getLogger } from '../logger';
import { startNewSession } from './session';
import type { SourceType, WebAppFormat } from '@snapchat/blizzard-schema';

const log = getLogger('lifecycle');

let isFirstResume = true;

/** Metadata about the trigger for a page open */
export interface PageOpenProps {
  /**
   * Whether the application is in focus. If not specified, we'll check document.hasFocus(). In most
   * cases, leave this unspecified - only override this if you have a custom definition of focus.
   */
  hasFocus?: boolean;

  /** Current format of this web application (e.g. if launched as a Chrome application). */
  appFormat?: WebAppFormat;

  /** ID of the notification which triggered this page open (if any) */
  notificationId?: string;

  /**
   * Source of the notification which triggered this page open (if any), e.g. "CHAT" = a chat
   * notification.
   */
  notificationType?: string;

  /** Where the user came into the page from (e.g. CONSUMER_WEB) */
  source?: SourceType;

  /** Name used to describe page type, e.g. PUBLIC_USER_PROFILE. */
  pageId?: string;
}

/**
 * Notify Blizzard that the application has been opened.
 *
 * This should be called as early as possible in the app lifecycle to ensure sessions are accurate.
 * "opened" means the page was (re)loaded (foreground/background) or brought into the foreground
 * (focused).
 *
 * Starts a new session (i.e. generates a new session ID), updates app startup type (COLD -> WARM)
 * and will log the WEB_PAGE_OPEN event in the future.
 *
 * @param props Metadata about this page open (if available), recorded on the WPO event.
 */
export function onPageOpen(props?: PageOpenProps): void {
  const isFocused = props?.hasFocus ?? document.hasFocus();

  log.info(
    'Blizzard - #onOpen',
    isFirstResume ? '(cold start)' : '(warm start)',
    isFocused ? '(foreground)' : '(background)'
  );

  // Update session
  if (isFirstResume) {
    // On the very first resume (COLD), we don't need to reset the session ID and we want to keep startup
    // type cold. The first session ID/startup type are set on the first call to getSessionProperties().
    isFirstResume = false;
  } else {
    startNewSession('WARM');
  }
}

/**
 * Notify Blizzard that the application has been closed. This either means the page is closed
 * completely, or is moved to the background.
 */
export function onPageClose(): void {
  log.info('Blizzard - #onClose');
  // TODO: this is where we will log WEB_PAGE_CLOSE.
}
